import { reactive, computed, watch } from "vue";

export const MAGNIFIER_DEFAULTS = {
    zoom: { current: 2, min: 1.5, max: 4, step: 0.5 },
    size: { current: 180, min: 140, max: 320, step: 20 },
    position: { step: 20 },
    mode: 'cursor'
};

export function normalizeZoom(value, zoomMin = MAGNIFIER_DEFAULTS.zoom.min, zoomMax = MAGNIFIER_DEFAULTS.zoom.max) {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return MAGNIFIER_DEFAULTS.zoom.current;
    return Math.min(zoomMax, Math.max(zoomMin, Math.round(numericValue * 10) / 10));
}

export function normalizeSize(value, sizeMin = MAGNIFIER_DEFAULTS.size.min, sizeMax = MAGNIFIER_DEFAULTS.size.max) {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return MAGNIFIER_DEFAULTS.size.current;
    return Math.min(sizeMax, Math.max(sizeMin, numericValue));
}

export function normalizeCoordinate(value, axis, size, viewportWidth, viewportHeight) {
    const halfSize = size / 2;
    const max = axis === "x" ? viewportWidth - halfSize : viewportHeight - halfSize;
    const min = halfSize;
    const fallback = axis === "x" ? viewportWidth / 2 : viewportHeight / 2;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return fallback;
    return Math.round(Math.min(max, Math.max(min, numericValue)));
}

export function calculateLensPosition(centerX, centerY, size) {
    const halfSize = size / 2;
    return { x: Math.round(centerX - halfSize), y: Math.round(centerY - halfSize) };
}

export function calculateSourceRegion(lensX, lensY, size, zoom, sourceWidth, sourceHeight, scaleX = 1, scaleY = 1) {
    const centerX = lensX + size / 2;
    const centerY = lensY + size / 2;
    const regionSize = size / zoom;
    let srcX = (centerX - regionSize / 2) * scaleX;
    let srcY = (centerY - regionSize / 2) * scaleY;
    let srcWidth = regionSize * scaleX;
    let srcHeight = regionSize * scaleY;
    if (srcX < 0) srcX = 0;
    if (srcY < 0) srcY = 0;
    if (srcX + srcWidth > sourceWidth) srcWidth = sourceWidth - srcX;
    if (srcY + srcHeight > sourceHeight) srcHeight = sourceHeight - srcY;
    return { srcX, srcY, srcWidth, srcHeight };
}

export function validateMode(mode) {
    const validModes = ['cursor', 'zone', 'pinned'];
    return validModes.includes(mode) ? mode : MAGNIFIER_DEFAULTS.mode;
}

export function getInitialPosition() {
    return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

export function useMagnifier({ magnifierViewRef, refreshSnapshot }) {
    const state = reactive({
        isMagnifierActive: false,
        magnifierZoom: 2,
        magnifierSize: 180,
        magnifierMode: "cursor",
        magnifierFixedX: window.innerWidth / 2,
        magnifierFixedY: window.innerHeight / 2,
        magnifierCursorPosition: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
        isMagnifierFrameVisible: true,
        isMagnifierSettingsMenuOpen: false,
        magnifierPinnedX: window.innerWidth / 2,
        magnifierPinnedY: window.innerHeight / 2,
        magnifierPinningEnabled: false,
        isMagnifierDragging: false,
    });

    const magnifierZoomMin = computed(() => MAGNIFIER_DEFAULTS.zoom.min);
    const magnifierZoomMax = computed(() => MAGNIFIER_DEFAULTS.zoom.max);
    const magnifierSizeMin = computed(() => MAGNIFIER_DEFAULTS.size.min);
    const magnifierSizeMax = computed(() => MAGNIFIER_DEFAULTS.size.max);
    const magnifierViewportWidth = computed(() => window.innerWidth);
    const magnifierViewportHeight = computed(() => window.innerHeight);

    function normalizeMagnifierFixedCoordinate(value, axis) {
        return normalizeCoordinate(
            value, axis,
            normalizeSize(state.magnifierSize),
            window.innerWidth, window.innerHeight
        );
    }

    function toggleMagnifier() {
        state.isMagnifierActive = !state.isMagnifierActive;
        refreshSnapshot(120);
    }

    function checkToggleMagnifier() {
        if (!state.isMagnifierActive) toggleMagnifier();
    }

    function toggleMagnifierFrame() {
        checkToggleMagnifier();
        state.isMagnifierFrameVisible = !state.isMagnifierFrameVisible;
    }

    function toggleMagnifierSettingsMenu() {
        state.isMagnifierSettingsMenuOpen = !state.isMagnifierSettingsMenuOpen;
        refreshSnapshot(380);
    }

    function setMagnifierMode(mode) {
        if (mode !== "cursor" && mode !== "zone") return;
        state.magnifierMode = mode;
        refreshSnapshot(120);
    }

    function changeMagnifierZoom(type) {
        checkToggleMagnifier();
        const defaults = MAGNIFIER_DEFAULTS;
        if (type === "minus") state.magnifierZoom = normalizeZoom(state.magnifierZoom - defaults.zoom.step);
        if (type === "more") state.magnifierZoom = normalizeZoom(state.magnifierZoom + defaults.zoom.step);
        refreshSnapshot(120);
    }

    function changeMagnifierSize(type) {
        checkToggleMagnifier();
        const defaults = MAGNIFIER_DEFAULTS;
        if (type === "minus") state.magnifierSize = normalizeSize(state.magnifierSize - defaults.size.step);
        if (type === "more") state.magnifierSize = normalizeSize(state.magnifierSize + defaults.size.step);
        refreshSnapshot(120);
    }

    function changeMagnifierFixedPosition(axis, type) {
        checkToggleMagnifier();
        const defaults = MAGNIFIER_DEFAULTS;
        const delta = type === "minus" ? -defaults.position.step : defaults.position.step;
        const current = axis === "x" ? state.magnifierFixedX : state.magnifierFixedY;
        const normalized = normalizeCoordinate(
            current + delta, axis,
            normalizeSize(state.magnifierSize),
            window.innerWidth, window.innerHeight
        );
        if (axis === "x") state.magnifierFixedX = normalized;
        else state.magnifierFixedY = normalized;
        refreshSnapshot(120);
    }

    function updateMagnifierFixedPosition(axis, value) {
        const normalized = normalizeCoordinate(
            value, axis,
            normalizeSize(state.magnifierSize),
            window.innerWidth, window.innerHeight
        );
        if (axis === "x") state.magnifierFixedX = normalized;
        else state.magnifierFixedY = normalized;
        refreshSnapshot(120);
    }

    function reset(preserveActive = false) {
        state.isMagnifierSettingsMenuOpen = false;
        state.isMagnifierFrameVisible = true;
        state.magnifierMode = "cursor";
        state.magnifierFixedX = window.innerWidth / 2;
        state.magnifierFixedY = window.innerHeight / 2;
        state.magnifierPinnedX = window.innerWidth / 2;
        state.magnifierPinnedY = window.innerHeight / 2;
        state.magnifierPinningEnabled = false;
        state.isMagnifierDragging = false;
        if (!preserveActive) state.isMagnifierActive = false;
        refreshSnapshot(200);
    }

    function applyFromProfile(entry) {
        if (entry.isMagnifierActive !== undefined) state.isMagnifierActive = !!entry.isMagnifierActive;
        if (entry.magnifierZoom) {
            state.magnifierZoom = normalizeZoom(entry.magnifierZoom);
        }
        if (entry.magnifierSize) {
            state.magnifierSize = normalizeSize(entry.magnifierSize);
        }
        if (entry.isMagnifierFrameVisible !== undefined) state.isMagnifierFrameVisible = !!entry.isMagnifierFrameVisible;
        if (entry.magnifierMode) setMagnifierMode(entry.magnifierMode);
        if (typeof entry.magnifierFixedX === "number") state.magnifierFixedX = normalizeMagnifierFixedCoordinate(entry.magnifierFixedX, "x");
        if (typeof entry.magnifierFixedY === "number") state.magnifierFixedY = normalizeMagnifierFixedCoordinate(entry.magnifierFixedY, "y");
    }

    watch(
        () => state.isMagnifierActive,
        (active) => {
            state.isMagnifierSettingsMenuOpen = !!active;
        }
    );

    return {
        state,
        magnifierViewRef,
        magnifierZoomMin,
        magnifierZoomMax,
        magnifierSizeMin,
        magnifierSizeMax,
        magnifierViewportWidth,
        magnifierViewportHeight,
        normalizeMagnifierFixedCoordinate,
        toggleMagnifier,
        checkToggleMagnifier,
        toggleMagnifierFrame,
        toggleMagnifierSettingsMenu,
        setMagnifierMode,
        changeMagnifierZoom,
        changeMagnifierSize,
        changeMagnifierFixedPosition,
        updateMagnifierFixedPosition,
        reset,
        applyFromProfile,
    };
}
